import React from 'react';
import PropTypes from 'prop-types';

/**
 * FIN-13003 — the ONE renderer for an open-case summary line.
 *
 * Merge-to-Case exists on two surfaces and each had grown its own card markup:
 *
 *   queue  (FramlMergeToCaseModal)  "C-732190 · Test · Age 80d · 2 alerts · Assigned"
 *   detail (MergeOpenCasesPicker)   "Case 732190Test80d2 alertsAssigned"
 *
 * The detail card laid its fields out as bare adjacent spans with only a left margin and no
 * separator, so they ran together into one unreadable string — the reported bug.
 *
 * The DATA was already unified: FIN-12996 / FIN-12954 gave both surfaces the same BE shape
 * (`FramlOpenCase`: caseId, caseName, status, statusLabel, department, ageDays, alertCount)
 * and added the alert count and status to the queue card. Only the presentation stayed
 * forked, so this is a formatting consolidation — no field reconciliation needed.
 *
 * The queue's format wins because the ticket cites it as correct. That means the detail card
 * also picks up two deliberate changes: the id reads `C-<id>` rather than `Case <id>`, and a
 * missing department renders an em-dash rather than vanishing (which previously made a
 * two-field card and a three-field card look alike).
 *
 * Presentational only — it renders the summary line, not the surrounding control. The queue
 * wraps it in a styled Radio and the detail in a clickable div; that difference is real and
 * stays with the callers.
 */

const Separator = () => (
  <span aria-hidden="true" style={{ color: '#9aa0aa', margin: '0 6px' }}>
    ·
  </span>
);

const Field = ({ children }) => (
  <span style={{ color: '#676f7e' }}>{children}</span>
);

Field.propTypes = { children: PropTypes.node };

const OpenCaseCardSummary = ({ openCase }) => {
  const { caseId, department, ageDays, alertCount, statusLabel, status } =
    openCase || {};

  // Status is the only optional segment: a case with no configured status label renders
  // without a trailing separator rather than ending in a dangling dot.
  const resolvedStatus = statusLabel || status;

  return (
    <>
      <span style={{ fontWeight: 600, color: '#1f2430' }}>C-{caseId}</span>
      <Separator />
      <Field>{department || '—'}</Field>
      <Separator />
      <Field>Age {ageDays}d</Field>
      <Separator />
      <Field>{alertCount ?? 0} alerts</Field>
      {resolvedStatus ? (
        <>
          <Separator />
          <Field>{resolvedStatus}</Field>
        </>
      ) : null}
    </>
  );
};

OpenCaseCardSummary.propTypes = {
  openCase: PropTypes.shape({
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    department: PropTypes.string,
    ageDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    alertCount: PropTypes.number,
    statusLabel: PropTypes.string,
    status: PropTypes.string
  }).isRequired
};

export default OpenCaseCardSummary;
